import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket täytyy käyttää WebSocketProvider:n sisällä');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, demoModeEnabled, elevatedChild } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = () => {
    if (!user) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    try {
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        if (ws.current !== socket) return;
        setConnected(true);
        reconnectAttempts.current = 0;
        socket.send(JSON.stringify({
          type: 'auth',
          userId: user.id,
          userType: user.type
        }));
      };

      socket.onmessage = (event) => {
        if (ws.current !== socket) return;
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          switch (data.type) {
            case 'task_completed':
              if (data.userId === user.id) {
                showTaskCompletionNotification(data.completion);
              }
              break;
            case 'shared_task_completed':
              window.dispatchEvent(new CustomEvent('sharedTaskCompleted', { detail: data }));
              break;
            case 'task_created':
            case 'task_updated':
            case 'task_deleted':
              window.dispatchEvent(new CustomEvent('tasksUpdated', { detail: data }));
              break;
            case 'bonus_claimed':
              window.dispatchEvent(new CustomEvent('bonus-claimed', { detail: data }));
              window.dispatchEvent(new CustomEvent('kiosk-data-updated'));
              break;
            case 'motion_detected':
              window.dispatchEvent(new CustomEvent('motionDetected', { detail: data }));
              break;
            case 'approval_notification':
              window.dispatchEvent(new CustomEvent('approvalNotification', { detail: data }));
              break;
            case 'shop_item_added':
              window.dispatchEvent(new CustomEvent('shopItemAdded', { detail: data }));
              break;
            case 'task_request_new':
              window.dispatchEvent(new CustomEvent('taskRequestNew', { detail: data }));
              break;
            case 'task_request_dismissed':
              window.dispatchEvent(new CustomEvent('taskRequestDismissed', { detail: data }));
              break;
            default:
              break;
          }
        } catch (error) {
          console.error('WebSocket viestin käsittely epäonnistui:', error);
        }
      };

      socket.onclose = (event) => {
        if (ws.current !== socket) return;
        setConnected(false);
        if (event.code !== 1000 && reconnectAttempts.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      socket.onerror = () => {
        if (ws.current !== socket) return;
        setConnected(false);
      };

    } catch (error) {
      console.error('WebSocket yhteyden luominen epäonnistui:', error);
    }
  };

  const disconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (ws.current) {
      ws.current.close(1000, 'Käyttäjä sulki yhteyden');
      ws.current = null;
    }
  };

  const sendMessage = (message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  };

  const showTaskCompletionNotification = (completion) => {
    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Tehtävä suoritettu! 🎉', {
        body: `${completion.title} - Ansaitsit ${completion.reward_points} pistettä!`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-192x192.png'
      });
    }

    // Dispatch custom event for UI animations
    window.dispatchEvent(new CustomEvent('taskCompleted', { 
      detail: { 
        completion,
        showAnimation: true 
      } 
    }));
  };

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Request notification permission on mount and auto-subscribe to push
  useEffect(() => {
    if (!user || demoModeEnabled) return;
    if (elevatedChild) return;
    if (user.username === 'kioski') return;
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          console.log('Ilmoituslupa:', permission);
          if (permission === 'granted') {
            import('../services/push').then(pushService => pushService.getPushStatus());
          }
        });
      } else if (Notification.permission === 'granted') {
        import('../services/push').then(pushService => pushService.getPushStatus());
      }
    }
  }, [user, demoModeEnabled, elevatedChild]);

  const value = {
    connected,
    lastMessage,
    sendMessage,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};